/**
 * Mapas status→label/cor/ícone por DOMÍNIO — fonte única (doc E clusters 9/10).
 * Renderização pronta: `components/StatusBadge.jsx` (`<StatusBadge domain status/>`).
 *
 * Domínios:
 *   - wms        : outbound/inbound do WMS (unifica DashboardPage × OutboundDetailsPage;
 *                  nas 2 divergências de cor, valem as cores do DashboardPage)
 *   - payment    : status de payment MP → variant do `ui/Badge`
 *   - promotion  : status de campanha/promoção ML (4 cópias idênticas unificadas)
 *   - promotionType : tipo de campanha ML → classes de cor (3 cópias unificadas)
 *   - purchasing : status de ordem de compra (OC)
 */
import { Calendar, Check, CheckCircle, Clock, Truck, Warning, XCircle } from 'shared/icons';

export const WMS_STATUS_CONFIG = {
    pending: {
        label: 'Solicitacao',
        color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
        icon: Warning,
    },
    rejected: {
        label: 'Cancelado',
        color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
        icon: Warning,
    },
    cancelled: {
        label: 'Cancelado',
        color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
        icon: Warning,
    },
    idle: {
        label: 'Aguardando separacao',
        color: 'bg-mono-100 text-mono-700 dark:bg-mono-900/40 dark:text-mono-200',
        icon: Clock,
    },
    picking: {
        label: 'Em separacao',
        color: 'bg-mono-100 text-mono-700 dark:bg-mono-900/40 dark:text-mono-200',
        icon: Truck,
    },
    ready_to_collect: {
        label: 'Pronto para coleta',
        color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
        icon: CheckCircle,
    },
    collected: {
        label: 'Coletado',
        color: 'bg-mono-100 text-mono-800 dark:bg-mono-800 dark:text-mono-200',
        icon: Check,
    },
    processing: {
        label: 'Processando...',
        color: 'bg-mono-200 text-mono-600 dark:bg-mono-800 dark:text-mono-400',
        icon: Clock,
    },
};

/** Status de payment (MercadoPago) → variant do `components/ui/Badge`. */
export const PAYMENT_STATUS_VARIANT = {
    approved: 'success',
    refunded: 'warning',
    in_mediation: 'warning',
    in_process: 'warning',
    pending: 'warning',
    authorized: 'warning',
    rejected: 'danger',
    cancelled: 'danger',
    charged_back: 'danger',
};

export const PROMOTION_STATUS_CONFIG = {
    started: {
        label: 'Ativa',
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        icon: CheckCircle,
    },
    candidate: {
        label: 'Candidata',
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        icon: Warning,
    },
    finished: {
        label: 'Finalizada',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
        icon: XCircle,
    },
    pending: {
        label: 'Pendente',
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        icon: Calendar,
    },
};

/** Tipo de campanha ML → classes de cor do badge (texto = o próprio tipo). */
export const PROMOTION_TYPE_COLORS = {
    DEAL: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    MARKETPLACE_CAMPAIGN: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    VOLUME: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
    DOD: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    LIGHTNING: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    PRICE_DISCOUNT: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
    PRE_NEGOTIATED: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
    SELLER_CAMPAIGN: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
    SMART: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
    PRICE_MATCHING: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    // F18: variante 100% custeada pelo ML; mesma família de cor, tom mais forte.
    PRICE_MATCHING_MELI_ALL: 'bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100',
    UNHEALTHY_STOCK: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
    SELLER_COUPON_CAMPAIGN: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
    BANK: 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200',
};

export const PROMOTION_TYPE_COLOR_FALLBACK = 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';

/** Status de OC (purchasing) → label pt-BR. */
export const PURCHASING_STATUS_LABELS = {
    DRAFT: 'Rascunho',
    APPROVAL: 'Aprovacao',
    OPEN: 'Aguardando fornecedor',
    PRODUCTION: 'Pedido feito',
    INVOICED: 'NF emitida',
    // "Fornecedor terminou tudo, estamos aguardando a mercadoria" — vale
    // tanto para o envio real quanto para o bypass (que estaciona aqui).
    SHIPPED: 'Aguardando recebimento',
    RECEIVED: 'Pedido recebido',
    CANCELLED: 'Cancelado',
};

const CONFIG_BY_DOMAIN = {
    wms: { map: WMS_STATUS_CONFIG, fallbackKey: 'pending' },
    promotion: { map: PROMOTION_STATUS_CONFIG, fallbackKey: 'pending' },
};

/**
 * Config `{label, color, icon}` de um status num domínio ('wms'|'promotion').
 * Status desconhecido → fallback do domínio (mesma semântica dos mapas locais
 * que existiam: WMS caía em `pending`, promoção caía em `pending`).
 */
export function GetStatusConfig(domain, status) {
    const entry = CONFIG_BY_DOMAIN[domain];
    if (!entry) return null;
    return entry.map[status] || entry.map[entry.fallbackKey];
}
